"use client"

import { useMemo, type PropsWithChildren } from "react"
import { MarkdownRenderer } from "./MarkdownRenderer"
import { DeepLiteratureEvidencePopover } from "./DeepLiteratureEvidencePopover"
import {
  ChatMessagePresentationProvider,
  type ChatMessagePresentation,
} from "./ChatMessagePresentation"
import {
  buildDeepLiteratureReferencesMarkdown,
  collectBiblioSourceMetadata,
  collectDeepLiteratureEvidenceChunks,
  evidenceChunkKey,
  formatDeepLiteratureEvidenceText,
  parseDeepLiteratureEvidenceHref,
} from "./deepLiteratureReferences"
import type { Message, MessageSegment } from "./types"

function messageTextSegments(message: Message): string[] {
  if (!message.segments?.length) return [message.content]

  return message.segments
    .filter((seg): seg is Extract<MessageSegment, { type: "text" }> => seg.type === "text")
    .map(seg => seg.content)
}

function DeepLiteratureMarkdown({
  content,
  message,
}: {
  content: string
  message: Message
}) {
  const sourceMetadata = useMemo(
    () => collectBiblioSourceMetadata(message.segments),
    [message.segments]
  )
  const evidenceChunks = useMemo(
    () => collectDeepLiteratureEvidenceChunks(message.segments),
    [message.segments]
  )

  return (
    <MarkdownRenderer
      content={content}
      renderLink={({ href, children }) => {
        const marker = parseDeepLiteratureEvidenceHref(href)
        if (!marker) return undefined

        const chunk =
          marker.kind === "chunk"
            ? evidenceChunks.get(evidenceChunkKey(marker.sourceId, marker.chunkId))
            : undefined

        return (
          <DeepLiteratureEvidencePopover
            marker={marker}
            chunk={chunk}
            sourceMetadata={sourceMetadata.get(marker.sourceId)}
          >
            {children}
          </DeepLiteratureEvidencePopover>
        )
      }}
    />
  )
}

export function DeepLiteratureMessagePresentationProvider({
  children,
}: PropsWithChildren) {
  const presentation = useMemo<ChatMessagePresentation>(
    () => ({
      formatAssistantText: (text) => formatDeepLiteratureEvidenceText(text),
      renderAssistantText: (text, message) => (
        <DeepLiteratureMarkdown content={text} message={message} />
      ),
      renderAssistantFooter: (message) => {
        if (message.isStreaming) return null

        const referencesMarkdown = buildDeepLiteratureReferencesMarkdown(
          messageTextSegments(message),
          collectBiblioSourceMetadata(message.segments)
        )

        if (!referencesMarkdown) return null

        return (
          <div className="mt-3 border-t border-gray-200 pt-2">
            <MarkdownRenderer content={referencesMarkdown} />
          </div>
        )
      },
    }),
    []
  )

  return (
    <ChatMessagePresentationProvider presentation={presentation}>
      {children}
    </ChatMessagePresentationProvider>
  )
}
